import React from 'react';
import { TextInput } from '@portal_components/components/TextInput';
import { Contract, CustomerSearch, Group } from '../types';
import { TableItem } from '@portal_components/components/TableItem';
import { CustomerSearchResult } from '../elements/customerSearch/CustomerSearchResult';
import { EnvironmentVariables } from '../EnvironmentVariables';
import axios from 'axios';
import { usePortalQuery, EHttpMethod } from '../hooks/usePortalQuery';
import { useForm, FormProvider } from 'react-hook-form';
import { FormButton } from '@portal_components/components/FormButton';
import { FormTextInput } from '@portal_components/components/FormTextInput';
import { FormSearchDropdown } from '@portal_components/components/FormSearchDropdown';
import { PageHeading } from '@portal_components/components/PageHeading';
import { Button } from '@portal_components/components/Button';
import { EUserRole } from '@portal_components/types';
import { useUserInfo } from '../hooks/useUserInfo';

export const CustomerSearchPage = () => {
  const { userInfo } = useUserInfo();
  const [searchResults, setSearchResults] = React.useState<{ group: Group; contracts: Contract[] }[] | null>(
    null
  );
  const [isSearchResultsLoading, setIsSearchResultsLoading] = React.useState<boolean>(false);
  const [expandedContractIndex, setExpandedContractIndex] = React.useState<number | null>(null);
  const [expandedGroupIndex, setExpandedGroupIndex] = React.useState<number | null>(null);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

  const { data: groupData, isLoading: isGroupDataLoading } = usePortalQuery<
    { customerGroupName: string; customerGroupId: string }[]
  >(
    EHttpMethod.GET,
    'search-groups',
    'search-groups',
    true,
    undefined,
    undefined,
    () => {
      setErrorMessage('An error occurred while loading groups.');
    },
    false,
    600000,
    600000
  );

  const methods = useForm<CustomerSearch>({
    values: {
      searchCustomerGroupId: '',
      searchSapNumber: ''
    }
  });

  const handleReset = () => {
    setSearchResults(null);
    setErrorMessage(null);
    methods.reset({
      searchCustomerGroupId: '',
      searchSapNumber: ''
    });
  };

  const handleSearch = async (values: CustomerSearch) => {
    if (isGroupDataLoading) return;
    setSearchResults(null);
    setErrorMessage(null);
    setIsSearchResultsLoading(true);

    if (!values.searchCustomerGroupId && !values.searchSapNumber) {
      setErrorMessage('Select customer group name or SAP number to search');
      setIsSearchResultsLoading(false);
      return;
    }

    const response = await axios
      .post<
        {
          group: Group;
          contracts: Contract[];
        }[]
      >(`${EnvironmentVariables.ApiUrl}/customer-search`, values, {
        headers: {
          'Content-Type': 'application/json'
        },
        withCredentials: true
      })
      .catch(error => error.response);
    setIsSearchResultsLoading(false);
    if (!response) {
      setErrorMessage('An error occurred while searching for customers.');
      return;
    }
    if (response.status !== 200) {
      setErrorMessage(response.data);
      return;
    }
    if (!response.data) {
      setErrorMessage('No results found.');
      return;
    }
    setSearchResults(response.data);
  };

  const handleExpandClicked = (index: number) => () => {
    setExpandedContractIndex(null);
    if (expandedGroupIndex === index) {
      setExpandedGroupIndex(null);
    } else {
      setExpandedGroupIndex(index);
    }
  };

  const renderCustomerSearchResults = (result: { group: Group; contracts: Contract[] }, index: number) => {
    return (
      <div className="mb-4" key={result.group.customerGroupId}>
        {searchResults && searchResults.length > 1 && (
          <div className="bg-elevation-1 px-4 py-2">
            <TableItem
              readonly
              onExpandClicked={handleExpandClicked(index)}
              expanded={expandedGroupIndex === index}
            >
              <TextInput
                title="Customer Name"
                initialValue={result.group.customerGroupName}
                readonly
                hideErrorControl
              />
              <TextInput
                title="Customer Type"
                initialValue={result.group.customerGroupTypeId}
                readonly
                hideErrorControl
              />
              <TextInput
                title="SAP Number"
                initialValue={result.group.sapCustomerNumber}
                readonly
                hideErrorControl
              />
            </TableItem>
          </div>
        )}
        {(expandedGroupIndex === index || (searchResults && searchResults.length === 1)) && (
          <CustomerSearchResult
            group={result.group}
            contracts={result.contracts}
            expandedContractIndex={expandedContractIndex}
            setExpandedContractIndex={setExpandedContractIndex}
          />
        )}
      </div>
    );
  };

  return (
    <div>
      <PageHeading
        title="Customer Search"
        navigationButtons={[
          {
            icon: '/add.svg',
            title: 'Add Group',
            path: '/addGroup',
            disabled: !userInfo || userInfo.role === EUserRole.READONLY_USER
          }
        ]}
      />
      <div className="grid grid-cols-12 gap-4">
        <div className="col-span-3 flex flex-col">
          <FormProvider {...methods}>
            <form onSubmit={methods.handleSubmit(handleSearch)}>
              <FormSearchDropdown<CustomerSearch>
                title="Customer Group Name"
                placeholder="Select..."
                name="searchCustomerGroupId"
                elementsForDisplay={
                  groupData && typeof groupData === 'object'
                    ? groupData.map(data => {
                        return {
                          title: `${data.customerGroupName} (ID:${data.customerGroupId})`,
                          value: data.customerGroupId
                        };
                      })
                    : []
                }
              />
              <FormTextInput<CustomerSearch> title="SAP Number" name="searchSapNumber" />
              <div className="flex flex-row justify-start gap-3">
                <FormButton type="submit" disabled={isGroupDataLoading || isSearchResultsLoading} primary>
                  Search
                </FormButton>
                <Button text="Reset" primary={false} onClick={handleReset} />
              </div>
            </form>
          </FormProvider>
          {errorMessage && <div className="pt-2 text-feedback-red">{errorMessage}</div>}
        </div>
        <div className="col-span-9">
          {isSearchResultsLoading && <div>Loading Search Results...</div>}
          {!isSearchResultsLoading && searchResults && searchResults.map(renderCustomerSearchResults)}
        </div>
      </div>
    </div>
  );
};
